import { z } from "zod";

export enum AssessmentType {
  SCHEDULED = "SCHEDULED",
  ASYNCHRONOUS = "ASYNCHRONOUS",
}

export const orientationVideoSchema = z.union([
  z.object({
    videoUrl: z.string(),
    videoPosterUrl: z.string().optional(),
    quiz: z.array(
      z.object({
        question: z.string(),
        options: z.array(
          z.object({
            text: z.string(),
            isCorrect: z.boolean(),
          })
        ),
      })
    ),
  }),
  // Old non-quiz schema. Will be removed in the future
  z.object({
    videoUrl: z.string(),
    videoPosterUrl: z.string().optional(),
    // @deprecated No longer used and will be removed in the future
    recapText: z.array(z.string()),

    // @deprecated No longer used and will be removed in the future
    confirmationText: z.string(),
  }),
]);

export type OrientationVideo = z.infer<typeof orientationVideoSchema>;

export const skillsAssessmentSchema = z.object({
  id: z.string(),
  attributes: z.object({
    qualificationId: z.string(),
    videoAsk: z.object({
      url: z.string(),
      numberOfQuestions: z.number(),
      estimatedTimeToCompleteInMinutes: z.number(),
    }),
    experimentDetails: z
      .object({
        // Don't want to use enum so that we can add new experiments without breaking the previous version of the app
        experimentType: z.string(),
        experimentMetadata: z.object({
          basketSize: z.number().optional(),
          leadTimeInHours: z.number().optional(),
          onTimeClipboardScoreAdjustment: z.number().optional(),
          actualScoreRewarded: z.number().optional(),

          // Used to display initial values so worker know what was initial state and what was the reward
          initialValues: z.object({
            basketSize: z.number().optional(),
            leadTimeInHours: z.number().optional(),
            initialScoreAdjustment: z.number().optional(),
          }),

          // Used to render screen that shows what would happen if worker is on time
          // These are optional as we don't have all of these attributes for experiment right now.
          // But we still keep them if in future we add them.
          onTimeReward: z.object({
            newShiftBasketSize: z.number().optional(),
            newShiftBookingLeadTimeInHours: z.number().optional(),
            clipboardScoreAdjustment: z.number().optional(),
          }),
        }),
      })
      .optional(),
    orientationVideos: z.array(orientationVideoSchema).optional(),
  }),
  type: z.literal("assessment"),
  relationships: z.object({
    assessmentStatus: z.object({
      data: z
        .object({
          id: z.string(),
          type: z.literal("assessment-status"),
        })
        .nullable(),
    }),
    latestScheduledEvent: z.object({
      data: z
        .object({
          id: z.string(),
          type: z.literal("assessment-event"),
        })
        .nullable(),
    }),
    qualification: z.object({
      data: z
        .object({
          id: z.string(),
          type: z.literal("qualification"),
        })
        .nullable(),
    }),
  }),
});
export type SkillsAssessment = z.infer<typeof skillsAssessmentSchema>;

export enum SkillsAssessmentWorkerStatus {
  PENDING = "PENDING",
  SUBMITTED = "SUBMITTED",
  UNDER_REVIEW = "UNDER_REVIEW",
  FAILED = "FAILED",
  PASSED = "PASSED",
}

export const skillsAssessmentStatusSchema = z.object({
  id: z.string(),
  attributes: z.object({
    type: z.nativeEnum(AssessmentType),
    status: z.nativeEnum(SkillsAssessmentWorkerStatus),
    dueDate: z.string().optional(),
    isAllowedToStartAssessment: z.boolean().optional(),
    /** only present if status is FAILED */
    retakeAllowedAfter: z.string().optional(),

    // We may not know this value until the orientation is scheduled.
    isStartedOnTime: z.boolean().optional(),
  }),
  type: z.literal("assessment-status"),
});
export type SkillsAssessmentStatus = z.infer<typeof skillsAssessmentStatusSchema>;

export const assessmentEventSchema = z.object({
  id: z.string(),
  attributes: z.object({
    status: z.string(),
    workerId: z.string(),
    assessmentId: z.string(),
    scheduledFor: z.string().datetime().optional(),
  }),
  relationships: z
    .object({
      assessmentStatus: z
        .object({
          data: z.object({
            id: z.string(),
            type: z.literal("assessment-status"),
          }),
        })
        .optional(),
    })
    .optional(),
  type: z.literal("assessment-event"),
});
export type AssessmentEvent = z.infer<typeof assessmentEventSchema>;
